/**
 * Membership module
 * Contains logic for membership/add view
 * @module
 */
import toArray from 'arrayify';
import BEM from 'bem.js';
import moment from 'moment';
import { BLOCK_PHOTO, PhotoUpload } from '../../components/photo-upload';
import { BLOCK_CHECKBOX, BLOCK_RADIO, MODIFIER_BUTTON, FakeControl } from '../../components/form';
import { MemberFactory, PriceConsumer } from '../../data/membership/price';


const BLOCK_FORMSET = 'formset';

const BLOCK_DATEPICKER = 'datepicker';

const ELEMENT_BODY = 'body';

const ELEMENT_TEMPLATE = 'template';

const ELEMENT_CONTAINER = 'container';

const ELEMENT_ADD = 'add';

const ELEMENT_REMOVE = 'remove';

const BLOCK_PRICES = 'prices';

const ELEMENT_MEMBER = 'member';

const ELEMENT_DISCOUNT = 'discount';

const ELEMENT_PRICE = 'price';

const ELEMENT_TOTAL = 'total';

const FORMSET = BEM.getBEMNode(BLOCK_FORMSET);

const FORMSET_BODY = BEM.getBEMNode(BLOCK_FORMSET, ELEMENT_BODY);

const TEMPLATE = BEM.getBEMNode(BLOCK_FORMSET, ELEMENT_TEMPLATE);

const ADD = BEM.getBEMNode(BLOCK_FORMSET, ELEMENT_ADD);

const REMOVE = BEM.getBEMNode(BLOCK_FORMSET, ELEMENT_REMOVE);

const PRICES_MEMBER = BEM.getBEMNode(BLOCK_PRICES, ELEMENT_MEMBER);

const PRICE_MEMBER = (PRICES_MEMBER) ? BEM.getChildBEMNode(PRICES_MEMBER, BLOCK_PRICES, ELEMENT_PRICE) : null;

const PRICES_DISCOUNT= BEM.getBEMNode(BLOCK_PRICES, ELEMENT_DISCOUNT);

const PRICE_DISCOUNT = (PRICES_DISCOUNT) ? BEM.getChildBEMNode(PRICES_DISCOUNT, BLOCK_PRICES, ELEMENT_PRICE) : null;

const PRICES_TOTAL= BEM.getBEMNode(BLOCK_PRICES, ELEMENT_TOTAL);

const PRICE_TOTAL= (PRICES_TOTAL) ? BEM.getChildBEMNode(PRICES_TOTAL, BLOCK_PRICES, ELEMENT_PRICE) : null;

const MATCH_FORM_IDS = /(id|for|name)="(.+?)"/g;

const PREFIX_PLACEHOLDER = '__prefix__';


/**
 * Formset class
 * Contains logic for the add member form
 * @class
 */
class Formset {
    /**
     * Constructor method
     * Gets called when class get instantiated
     */
    constructor() {
        this.memberFactory = new MemberFactory();
        this.priceConsumer = new PriceConsumer();

        this.setUpAddForm();
        this.setUpRemoveForm();
        this.setUpUpdatePrice();
        this.updateButton();
        setTimeout(this.updatePrice.bind(this), 0);
    }

    /**
     * Binds ADD click to this.addForm()
     */
    setUpAddForm() {
        ADD.addEventListener('click', (e) => {
            e.preventDefault();
            this.addForm();
        });
    }

    /**
     * Binds REMOVE click to this.removeForm()
     */
    setUpRemoveForm() {
        REMOVE.addEventListener('click', (e) => {
            e.preventDefault();
            this.removeForm();
        });
    }

    /**
     * Binds visit/datepicker change to this.updatePrice()
     */
    setUpUpdatePrice() {
        FORMSET_BODY.addEventListener('change', (e) => {
            let node = e.target;
            if (node.className.match(BEM.getBEMClassName(BLOCK_RADIO, false, MODIFIER_BUTTON)) ||
                node.className.match(BLOCK_DATEPICKER)) {
                this.updatePrice();
            }
        });
    }

    /**
     * Creates a new form based on TEMPLATE
     * Updates the id's of the form to unique values
     * Applies styling to fake elements (checkboxes, radio buttons, datepicker)
     */
    addForm() {
        // Creates a new form based on TEMPLATE
        let template = document.importNode(TEMPLATE.content, true);
        FORMSET_BODY.appendChild(template);
        let form = FORMSET_BODY.children[FORMSET_BODY.children.length -1];

        // Updates the id's of the form to unique values
        form.innerHTML = form.innerHTML.replace(MATCH_FORM_IDS, this.updateMatchedId.bind(this, form));

        // Applies styling to fake elements (checkboxes, radio buttons, datepicker)
        let checkboxes = BEM.getChildBEMNodes(form, BLOCK_CHECKBOX);
        let radios = BEM.getChildBEMNodes(form, BLOCK_RADIO);
        let photo = BEM.getChildBEMNode(form, BLOCK_PHOTO);

        new FakeControl(checkboxes);
        new FakeControl(radios);
        new PhotoUpload(photo);

        let index = FORMSET_BODY.children.length;
        FORMSET.querySelector('[name="form-TOTAL_FORMS"]').value = index;
        this.updateButton();
    }

    /**
     * Callback function for replacing ids in form
     * Replaces PREFIX_PLACEHOLDER with index of form
     * @param {HTMLFormElement} form The form we're replacing id's for
     * @param {string} match The string matching MATCH_FORM_IDS
     * @param {string} attr (Capturing group) the matched attribute
     * @param {string} id (Capturing group) the value of the attr
     * @returns {string} An html attribute/value pair with PREFIX_PLACEHOLDER replaced
     */
    updateMatchedId(form, match, attr, id) {  // jshint unused:false
        let index = toArray(FORMSET_BODY.children).indexOf(form);
        id = id.replace(PREFIX_PLACEHOLDER, index);
        return `${attr}="${id}"`;
    }

    /**
     * Removes the last form from FORMSET_BODY
     */
    removeForm() {
        let index = FORMSET_BODY.children.length -1;
        let child = FORMSET_BODY.children[index];

        if (child) {
            FORMSET_BODY.removeChild(child);
            this.updatePrice();
        }

        FORMSET.querySelector('[name="form-TOTAL_FORMS"]').value = index;
        this.updateButton();
    }

    updateButton() {
        let index = FORMSET_BODY.children.length;
        if(index === 1) {
            REMOVE.classList.add('button--hidden');
        } else {
            REMOVE.classList.remove('button--hidden');
        }
    }

    /**
     * Updates the price information by performing an API request
     * Creates an array of Member instances
     * Excludes member without birthdate
     * Calls API to get price for matching members
     */
    updatePrice() {
        let memberForms = BEM.getChildBEMNodes(FORMSET_BODY, BLOCK_FORMSET, ELEMENT_CONTAINER);
        let members = [];

        // Creates an array of Member instances
        toArray(memberForms).map((memberForm) => {
            let birthdate = BEM.getChildBEMNode(memberForm, BLOCK_DATEPICKER);
            let visit = memberForm.querySelector('input[type="radio"]:checked');
            if(moment(birthdate.value, 'YYYY-MM-DD', true).isValid()){
                if (birthdate && visit) {
                    members.push(this.memberFactory.addMember({ birthdate: birthdate.value, visit: visit.value }));
                }
            }
        });
        // Excludes member without birthdate
        members = members.filter((member) => member.birthdate && member.visit);
        // Calls API to get price for matching members
        if (members.length) {
            this.priceConsumer.getPriceForMembers(members)
                .then(this.renderPrice.bind(this))
                .catch(console.error);  // jshint ignore:line
        }
    }

    /**
     * Updates DOM with new pricing information
     * @param {Price} price
     */
    renderPrice(price) {
        PRICE_MEMBER.innerText = price.sub_total.replace('.', ',');
        PRICE_DISCOUNT.innerText = price.discount.replace('.', ',');
        PRICE_TOTAL.innerText = price.total.replace('.', ',');
    }
}


if (ADD) {
    new Formset();
}



// WEBPACK FOOTER //
// ./src/my_artis/js/views/membership/add.js